import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'
import RaidPlanCandidatePicker from '../../TeamPlans/components/RaidPlanCandidatePicker'
import VisualPlanThumbnail from '../../TeamPlans/components/VisualPlanThumbnail'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { Badge } from '@/components/ui/badge'
import { Loader2, Plus, TriangleAlert } from 'lucide-react'
import Card from 'app/frontend/base/components/Card'
import { createRaidPlan } from 'app/frontend/base/api/raid_plans'
import { createVisualPlan } from 'app/frontend/base/api/visual_plans'
import { useTeam } from 'app/frontend/base/config/context/team'
import LinkPlanEncounterCard, {
  LinkPlanEncounterOption,
} from './LinkPlanEncounterCard'
import { useVisualPlansEnabledForEncounter } from '../../TeamPlans/visualPlansAvailability'

dayjs.extend(RelativeTime)

export type LinkActivityPlanDialogMode =
  | { kind: 'add' }
  | { kind: 'replace'; planType: 'raid' | 'visual'; planId: number }

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: LinkActivityPlanDialogMode
  raidPlans: RaidPlan[]
  visualPlans: VisualPlan[]
  linkedRaidPlanIds: number[]
  linkedVisualPlanIds: number[]
  encounterId: number
  difficulty: Difficulty
  teamPath: string
  encounters?: LinkPlanEncounterOption[]
  selectedEncounterId?: number
  onSelectEncounter?: (encounterId: number) => void
  onSelectRaidPlan: (plan: RaidPlan) => void
  onSelectVisualPlan: (plan: VisualPlan) => void
  onUnlinkRaidPlan: (planId: number) => void
  onUnlinkVisualPlan: (planId: number) => void
}

const VisualPlanCandidatePicker = ({
  plans,
  selectedPlanId,
  linkedPlanIds,
  warningDifficulty,
  onSelect,
  onUnlink,
}: {
  plans: VisualPlan[]
  selectedPlanId?: number | null
  linkedPlanIds: number[]
  warningDifficulty?: Difficulty
  onSelect: (plan: VisualPlan) => void
  onUnlink?: (plan: VisualPlan) => void
}) => {
  const linkedPlanIdSet = new Set(linkedPlanIds)

  if (!plans.length) {
    return (
      <p className='text-sm text-muted-foreground'>
        No visual plans found for this encounter
      </p>
    )
  }

  return (
    <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
      {plans.map((plan) => {
        const hasDifficultyWarning =
          warningDifficulty !== undefined &&
          plan.difficulty !== warningDifficulty
        const isLinked = linkedPlanIdSet.has(plan.id)
        const isSelected = plan.id === selectedPlanId
        const canSelect = !isLinked || isSelected
        return (
          <Card
            key={plan.id}
            role={canSelect ? 'button' : undefined}
            tabIndex={canSelect ? 0 : undefined}
            className={cn(
              'h-full flex flex-col gap-2 p-2 border transition-colors',
              isSelected || isLinked ? 'border-primary' : 'border-border',
              canSelect
                ? 'cursor-pointer hover:border-primary'
                : 'cursor-default',
            )}
            onClick={() => {
              if (!canSelect || isLinked) return
              onSelect(plan)
            }}
            onKeyDown={(event) => {
              if (!canSelect || isLinked) return
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect(plan)
              }
            }}
          >
            <div className='flex items-center justify-between gap-2 w-full'>
              <div className='flex items-center gap-2 min-w-0'>
                {isLinked ? (
                  <Badge variant='success' className='shrink-0'>
                    Linked
                  </Badge>
                ) : null}
                {hasDifficultyWarning ? (
                  <Badge variant='warning' className='gap-1 shrink-0'>
                    <TriangleAlert className='h-3 w-3' />
                    {difficultiesLabels[plan.difficulty]}
                  </Badge>
                ) : null}
                <span className='truncate font-medium'>{plan.name}</span>
              </div>
              {isLinked && onUnlink ? (
                <Button
                  type='button'
                  variant='outline'
                  size='xs'
                  onClick={(event) => {
                    event.stopPropagation()
                    onUnlink(plan)
                  }}
                >
                  Unlink
                </Button>
              ) : (
                <span className='text-xs text-muted-foreground whitespace-nowrap'>
                  {dayjs(plan.createdAt).fromNow()}
                </span>
              )}
            </div>
            <VisualPlanThumbnail visual={plan} embedded className='flex-1' />
          </Card>
        )
      })}
    </div>
  )
}

const sortLinkedFirst = <T extends { id: number }>(
  plans: T[],
  linkedPlanIds: number[],
) => {
  const linkedPlanIdSet = new Set(linkedPlanIds)
  return [...plans].sort((left, right) => {
    const leftLinked = linkedPlanIdSet.has(left.id)
    const rightLinked = linkedPlanIdSet.has(right.id)
    if (leftLinked !== rightLinked) return leftLinked ? -1 : 1
    return 0
  })
}

const LinkActivityPlanDialog = ({
  open,
  onOpenChange,
  mode,
  raidPlans,
  visualPlans,
  linkedRaidPlanIds,
  linkedVisualPlanIds,
  encounterId,
  difficulty,
  teamPath,
  encounters,
  selectedEncounterId,
  onSelectEncounter,
  onSelectRaidPlan,
  onSelectVisualPlan,
  onUnlinkRaidPlan,
  onUnlinkVisualPlan,
}: Props) => {
  const { team } = useTeam()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const visualPlansEnabled = useVisualPlansEnabledForEncounter(encounterId)
  const defaultTab =
    mode.kind === 'replace' && mode.planType === 'visual' ? 'visual' : 'raid'
  const [tab, setTab] = useState<'raid' | 'visual'>(defaultTab)
  const selectedRaidPlanId =
    mode.kind === 'replace' && mode.planType === 'raid' ? mode.planId : null
  const selectedVisualPlanId =
    mode.kind === 'replace' && mode.planType === 'visual' ? mode.planId : null
  const activeTab = !visualPlansEnabled
    ? 'raid'
    : mode.kind === 'replace'
    ? mode.planType === 'visual'
      ? 'visual'
      : 'raid'
    : tab
  const sortedRaidPlans = useMemo(
    () => sortLinkedFirst(raidPlans, linkedRaidPlanIds),
    [linkedRaidPlanIds, raidPlans],
  )
  const sortedVisualPlans = useMemo(
    () => sortLinkedFirst(visualPlans, linkedVisualPlanIds),
    [linkedVisualPlanIds, visualPlans],
  )
  const owner = { ownerType: 'team' as const, ownerId: team.id }
  const showEncounterSelector =
    mode.kind === 'add' && (encounters?.length ?? 0) > 1

  useEffect(() => {
    if (!open) return

    const refreshPlans = () => {
      void queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
      if (visualPlansEnabled) {
        void queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
      }
    }

    refreshPlans()

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshPlans()
    }

    window.addEventListener('focus', refreshPlans)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('focus', refreshPlans)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [open, queryClient, visualPlansEnabled])

  const createRaidMutation = useMutation({
    mutationFn: async () => {
      return await createRaidPlan(owner, {
        encounter_id: encounterId,
        difficulty,
        name: t('dashboard.team.plans.default-name'),
        timeline: { actors: [], phase_durations: {} },
      })
    },
    onSuccess: async (plan) => {
      await queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
      await queryClient.invalidateQueries({ queryKey: ['team'] })
      window.open(`${teamPath}/events/plans/${plan.id}`, '_blank')
    },
  })

  const createVisualMutation = useMutation({
    mutationFn: async () => {
      return await createVisualPlan(owner, {
        encounter_id: encounterId,
        difficulty,
        name: t('dashboard.team.plans.visuals.default-name'),
        screens: [{ id: crypto.randomUUID(), image_index: 0, shapes: [] }],
      })
    },
    onSuccess: async (plan) => {
      await queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
      window.open(`${teamPath}/events/plans/visuals/${plan.id}`, '_blank')
    },
  })

  const isCreating =
    createRaidMutation.isPending || createVisualMutation.isPending

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen)
        if (nextOpen) setTab(defaultTab)
      }}
    >
      <DialogContent
        className={showEncounterSelector ? 'max-w-4xl' : 'max-w-lg'}
      >
        <DialogHeader>
          <DialogTitle>
            {mode.kind === 'replace' ? 'Change linked plan' : 'Link plan'}
          </DialogTitle>
        </DialogHeader>
        {showEncounterSelector && encounters && onSelectEncounter ? (
          <div className='mb-1 flex min-w-0 flex-wrap gap-1.5 sm:flex-nowrap'>
            {encounters.map((encounter) => (
              <LinkPlanEncounterCard
                key={encounter.activityId}
                encounter={encounter}
                isActive={encounter.encounterId === selectedEncounterId}
                onClick={() => onSelectEncounter(encounter.encounterId)}
              />
            ))}
          </div>
        ) : null}
        {visualPlansEnabled ? (
          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              if (mode.kind === 'replace') return
              setTab(value as 'raid' | 'visual')
            }}
          >
            <TabsList className='grid w-full grid-cols-2'>
              <TabsTrigger value='raid' disabled={mode.kind === 'replace'}>
                Assignment & cooldown plans
              </TabsTrigger>
              <TabsTrigger value='visual' disabled={mode.kind === 'replace'}>
                Visual plans
              </TabsTrigger>
            </TabsList>
            <TabsContent value='raid' className='mt-4 space-y-3'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='w-full gap-1.5'
                disabled={isCreating}
                onClick={() => createRaidMutation.mutate()}
              >
                {createRaidMutation.isPending ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <Plus className='h-4 w-4' />
                )}
                Create new plan
              </Button>
              <div
                className={cn(
                  showEncounterSelector
                    ? 'grid grid-cols-1 gap-2 sm:grid-cols-2'
                    : 'space-y-2',
                )}
              >
                <RaidPlanCandidatePicker
                  plans={sortedRaidPlans}
                  selectedPlanId={selectedRaidPlanId}
                  linkedPlanIds={linkedRaidPlanIds}
                  warningDifficulty={difficulty}
                  teamPath={teamPath}
                  onSelect={onSelectRaidPlan}
                  onUnlink={(plan) => onUnlinkRaidPlan(plan.id)}
                  cardClassName={showEncounterSelector ? 'h-full' : undefined}
                />
              </div>
            </TabsContent>
            <TabsContent value='visual' className='mt-4 space-y-3'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='w-full gap-1.5'
                disabled={isCreating}
                onClick={() => createVisualMutation.mutate()}
              >
                {createVisualMutation.isPending ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <Plus className='h-4 w-4' />
                )}
                Create new plan
              </Button>
              <VisualPlanCandidatePicker
                plans={sortedVisualPlans}
                selectedPlanId={selectedVisualPlanId}
                linkedPlanIds={linkedVisualPlanIds}
                warningDifficulty={difficulty}
                onSelect={onSelectVisualPlan}
                onUnlink={(plan) => onUnlinkVisualPlan(plan.id)}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <div className='space-y-3'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='w-full gap-1.5'
              disabled={isCreating}
              onClick={() => createRaidMutation.mutate()}
            >
              {createRaidMutation.isPending ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <Plus className='h-4 w-4' />
              )}
              Create new plan
            </Button>
            <div
              className={cn(
                showEncounterSelector
                  ? 'grid grid-cols-1 gap-2 sm:grid-cols-2'
                  : 'space-y-2',
              )}
            >
              <RaidPlanCandidatePicker
                plans={sortedRaidPlans}
                selectedPlanId={selectedRaidPlanId}
                linkedPlanIds={linkedRaidPlanIds}
                warningDifficulty={difficulty}
                teamPath={teamPath}
                onSelect={onSelectRaidPlan}
                onUnlink={(plan) => onUnlinkRaidPlan(plan.id)}
                cardClassName={showEncounterSelector ? 'h-full' : undefined}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default LinkActivityPlanDialog
